import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Loader2, ArrowUpRight } from "lucide-react";
import { MaskedLine, FadeUp } from "./Reveal";
import { BUSINESS, SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", phone: "", service: "", message: "" };

const inputCls =
  "w-full border-b border-line bg-transparent py-3 text-base outline-none transition-colors duration-300 focus:border-ink placeholder:text-taupe/60";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you — your enquiry has been received. We'll reach out shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="mx-auto max-w-[1400px] px-6 py-12 lg:px-12 lg:py-28">
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-24">
        <div>
          <FadeUp>
            <p className="mb-10 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-taupe">
              <span className="inline-block h-2 w-2 bg-terra" />
              Contact
            </p>
          </FadeUp>
          <h2 className="font-serif text-4xl leading-[1.08] tracking-tighter sm:text-5xl lg:text-6xl">
            <MaskedLine>Tell us about</MaskedLine>
            <MaskedLine delay={0.12}>
              <em className="text-terra">your space.</em>
            </MaskedLine>
          </h2>
          <FadeUp delay={0.2}>
            <div data-testid="contact-address" className="mt-8 flex gap-4 border-t border-line pt-8">
              <MapPin size={20} className="mt-1 shrink-0 text-terra" />
              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-taupe">
                  Studio & workshop
                </p>
                <p className="max-w-sm text-base leading-relaxed">{BUSINESS.address}</p>
              </div>
            </div>
            <div data-testid="contact-phone" className="mt-6 flex gap-4">
              <Phone size={20} className="mt-1 shrink-0 text-terra" />
              <div>
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.3em] text-taupe">
                  Call or WhatsApp
                </p>
                <div className="flex flex-col gap-1.5">
                  <a
                    href={BUSINESS.phoneHref}
                    data-testid="contact-phone-link"
                    className="text-base transition-colors duration-300 hover:text-terra"
                  >
                    {BUSINESS.phone}
                  </a>
                  <a
                    href="tel:+919582997025"
                    data-testid="contact-phone-link-2"
                    className="text-base transition-colors duration-300 hover:text-terra"
                  >
                    +91 95829 97025
                  </a>
                </div>
              </div>
            </div>
            <div className="map-frame mt-6 overflow-hidden border border-line" data-testid="contact-map">
              <iframe
                title="STAR AVG ENTERPRISE location map"
                src={BUSINESS.mapsEmbed}
                className="h-64 w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </FadeUp>
        </div>
        <FadeUp delay={0.15} className="flex flex-col justify-center">
          <form data-testid="contact-form" onSubmit={submit} className="space-y-8">
            <div>
              <label htmlFor="enquiry-name" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.3em] text-taupe">
                Your name
              </label>
              <input
                id="enquiry-name"
                data-testid="enquiry-name-input"
                required
                maxLength={120}
                value={form.name}
                onChange={set("name")}
                placeholder="Full name"
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="enquiry-phone" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.3em] text-taupe">
                Phone
              </label>
              <input
                id="enquiry-phone"
                data-testid="enquiry-phone-input"
                required
                type="tel"
                maxLength={20}
                value={form.phone}
                onChange={set("phone")}
                placeholder="+91 ..."
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="enquiry-service" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.3em] text-taupe">
                Service interest
              </label>
              <select
                id="enquiry-service"
                data-testid="enquiry-service-select"
                required
                value={form.service}
                onChange={set("service")}
                className={`${inputCls} cursor-pointer appearance-none`}
              >
                <option value="" disabled>
                  Select a service
                </option>
                {SERVICES.map((s) => (
                  <option key={s.n} value={s.title}>
                    {s.title}
                  </option>
                ))}
                <option value="General enquiry">General enquiry</option>
              </select>
            </div>
            <div>
              <label htmlFor="enquiry-message" className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.3em] text-taupe">
                About your project
              </label>
              <textarea
                id="enquiry-message"
                data-testid="enquiry-message-input"
                required
                rows={4}
                maxLength={2000}
                value={form.message}
                onChange={set("message")}
                placeholder="Room sizes, scope, timeline — anything that helps"
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              type="submit"
              data-testid="enquiry-submit-button"
              disabled={sending}
              className="group flex w-full items-center justify-center gap-3 bg-ink px-10 py-5 text-xs font-semibold uppercase tracking-[0.2em] text-paper transition-colors duration-300 hover:bg-clay hover:text-ink disabled:cursor-not-allowed disabled:opacity-60 sm:w-fit"
            >
              {sending ? (
                <>
                  Sending
                  <Loader2 size={15} className="animate-spin" />
                </>
              ) : (
                <>
                  Send enquiry
                  <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </>
              )}
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
}
